import { useMemo } from "react";
import { useMutation, useQuery } from "convex/react";
import { shouldQueryProjectId } from "./useProjects";
import type { ChatboxHostStyle } from "@/lib/chatbox-client-style";
import type {
  ChatUiSettings,
  ChatboxFeedbackDialogSettings,
  ChatboxWelcomeDialogSettings,
} from "@/types/chatUi";

export type {
  ChatUiSettings,
  ChatboxFeedbackDialogSettings,
  ChatboxWelcomeDialogSettings,
};

export type ChatboxMode =
  | "anyone_with_link"
  | "invited_only"
  | "project_members";

export interface ChatboxMember {
  _id: string;
  chatboxId: string;
  projectId: string;
  email: string;
  userId?: string;
  role: "chat";
  invitedBy: string;
  invitedAt: number;
  revokedAt?: number;
  acceptedAt?: number;
  user: {
    _id: string;
    name: string;
    email: string;
    imageUrl: string;
  } | null;
}

export interface ChatboxServerSettings {
  serverId: string;
  serverName: string;
  useOAuth: boolean;
  serverUrl: string | null;
  clientId: string | null;
  oauthScopes: string[] | null;
  /** When true, server is not connected until the tester enables it (off by default). */
  optional?: boolean;
}

export interface ChatboxSettings {
  chatboxId: string;
  projectId: string;
  name: string;
  description?: string;
  /** Projected from the resolved host's hostConfig DTO. */
  hostStyle: ChatboxHostStyle;
  systemPrompt: string;
  modelId: string;
  temperature: number;
  requireToolApproval: boolean;
  allowGuestAccess: boolean;
  mode: ChatboxMode;
  /** Chat UI config envelope: welcome / feedback dialog surfaces (and future surfaces / branding). */
  chatUi?: ChatUiSettings | null;
  servers: ChatboxServerSettings[];
  /** The named host this chatbox resolves through. */
  namedHostId: string;
  namedHostName: string;
  /**
   * Environment identity + resolution state, same contract as
   * `ChatboxListItem`: non-null `environmentId` marks a live-follow row,
   * `environmentError` says why it can't resolve. Every settings-returning
   * mutation carries these too (mcpjam-backend #889), so state replaced from
   * a `setChatboxMode` response keeps them.
   */
  environmentId?: string | null;
  environmentName?: string | null;
  environmentError?: { code: string | null; message: string } | null;
  link: {
    token: string;
    path: string;
    url: string;
    rotatedAt: number;
    updatedAt: number;
  } | null;
  members: ChatboxMember[];
  /**
   * Secure Guest Harness Enablement — the per-swarm host-funded guest execution
   * opt-in + caps, read-only advisory state for the admin publish UI. `null`
   * (or absent) ⇒ guest execution disabled. Written via
   * `chatboxes:setChatboxGuestExecution` (project-admin gated).
   */
  guestExecution?: GuestExecutionSettings | null;
}

export interface GuestExecutionSettings {
  enabled: boolean;
  computerEnabled: boolean;
  sharedSkillsEnabled: boolean;
  dailyCreditCap: number;
  dailyComputerStartCap: number;
  maxConcurrentComputers: number;
  harnessEnabled?: boolean;
  dailyHarnessSpendCapMicros?: number;
  dailyHarnessCallCap?: number;
  maxConcurrentHarnessRuns?: number;
}

export interface ChatboxListItem {
  chatboxId: string;
  projectId: string;
  name: string;
  description?: string;
  hostStyle: ChatboxHostStyle;
  mode: ChatboxMode;
  allowGuestAccess: boolean;
  serverCount: number;
  serverNames: string[];
  /** The named host this chatbox resolves through. */
  namedHostId: string;
  namedHostName: string;
  /**
   * Phase 5: non-null iff this chatbox is environment-backed (live-follow,
   * mcpjam-backend #805) — for those rows `namedHostId`/`namedHostName` are
   * display-only. Optional so a backend predating the field reads as
   * host-backed.
   */
  environmentId?: string | null;
  /**
   * The environment's name, resolved live (mcpjam-backend #889). Null on
   * host-backed rows — absence means "not environment-backed", never
   * "unnamed". Optional so a backend predating the field reads as absent.
   */
  environmentName?: string | null;
  /**
   * Why an environment-backed row can't resolve right now: its environment
   * was archived, a pinned plugin was disabled, its host is gone. Null when
   * healthy AND on host-backed rows. The row is still projected — a scenario
   * whose share link is live must stay visible to the person who can retire
   * it — so this is what tells the UI to badge it instead of rendering a
   * confident-looking but empty row.
   */
  environmentError?: { code: string | null; message: string } | null;
  /** Shareable link (null until first publish mints one). */
  link?: { token: string; path: string; url: string } | null;
  /**
   * Scenario-list activity columns, maintained as write-time counters on the
   * chatbox row (mcpjam-backend). Optional so a deployment predating them
   * renders "—" rather than a confident zero — the two are different claims.
   * Excludes preview and synthetic sessions: a tester is someone who opened
   * the share link.
   *
   * `sessionCount` has no column of its own — it is read by
   * `isDeliberateScenario`, which uses any real tester history as evidence
   * that a row is a scenario someone made rather than a client's auto-minted
   * publish surface.
   */
  uniqueTesterCount?: number;
  sessionCount?: number;
  lastSessionAt?: number | null;
  createdAt: number;
  updatedAt: number;
}

export function useChatboxList({
  isAuthenticated,
  projectId,
}: {
  isAuthenticated: boolean;
  projectId: string | null;
}) {
  // Skip until `projectId` is a real Convex id: a transient LOCAL id (UUID or
  // `local_`/`project_` placeholder) is truthy but throws an
  // ArgumentValidationError against `listChatboxes` during project hydration —
  // the same guard useHostList uses.
  const enabled = isAuthenticated && shouldQueryProjectId(projectId);
  const chatboxes = useQuery(
    "chatboxes:listChatboxes" as any,
    enabled ? ({ projectId } as any) : "skip",
  ) as ChatboxListItem[] | undefined;

  return {
    chatboxes,
    isLoading: enabled && chatboxes === undefined,
  };
}

/**
 * The scenario published from one environment, or null when it isn't
 * published. Derived from the shared list subscription rather than its own
 * query — two subscriptions to the same rows is two chances to disagree.
 *
 * `undefined` while the list is still loading: "not published" and "we don't
 * know yet" send a viewer to different places.
 */
export function useEnvironmentChatbox({
  isAuthenticated,
  projectId,
  environmentId,
}: {
  isAuthenticated: boolean;
  projectId: string | null;
  environmentId: string | null;
}): { chatbox: ChatboxListItem | null | undefined; isLoading: boolean } {
  const { chatboxes, isLoading } = useChatboxList({
    isAuthenticated,
    projectId,
  });
  const chatbox = useMemo(() => {
    if (!environmentId || chatboxes === undefined) return undefined;
    return chatboxes.find((row) => row.environmentId === environmentId) ?? null;
  }, [chatboxes, environmentId]);
  return { chatbox, isLoading };
}

/**
 * Publishing an environment as a User Testing scenario, and retiring it.
 *
 * Both are PROJECT-ADMIN gated backend-side (a scenario is shared mutable
 * execution config, and a spend surface) — surface the `FORBIDDEN` copy
 * verbatim rather than paraphrasing it.
 *
 * Publish is idempotent: one scenario per environment, and a second call
 * returns the existing row with `created: false` and IGNORES the overrides,
 * so re-publishing can never silently rename or re-mode a live scenario
 * (mcpjam-backend #887).
 */
export function useEnvironmentChatboxMutations() {
  const publishEnvironmentChatbox = useMutation(
    "chatboxes:publishEnvironmentChatbox" as any,
  ) as (args: {
    environmentId: string;
    name?: string;
    description?: string;
    mode?: ChatboxMode;
  }) => Promise<{
    chatboxId: string;
    environmentId: string;
    name: string;
    mode: ChatboxMode;
    accessVersion: number;
    link: { token: string; path: string; url: string } | null;
    created: boolean;
  }>;
  const unpublishEnvironmentChatbox = useMutation(
    "chatboxes:unpublishEnvironmentChatbox" as any,
  ) as (args: {
    environmentId: string;
  }) => Promise<{ deleted: boolean; chatboxId?: string }>;
  return { publishEnvironmentChatbox, unpublishEnvironmentChatbox };
}

export function useChatbox({
  isAuthenticated,
  chatboxId,
}: {
  isAuthenticated: boolean;
  chatboxId: string | null;
}) {
  const chatbox = useQuery(
    "chatboxes:getChatbox" as any,
    isAuthenticated && chatboxId ? ({ chatboxId } as any) : "skip",
  ) as ChatboxSettings | null | undefined;

  return {
    chatbox,
    isLoading: isAuthenticated && !!chatboxId && chatbox === undefined,
  };
}

/**
 * Resolve the chatbox bound to a host. Under the 1:1 invariant
 * (`hosts.createHost` auto-mints a chatbox; `hosts.deleteHost` cascades),
 * every host has exactly one chatbox reachable via this query. The new
 * host-detail surfaces use this to drill `chatboxId` into the publish /
 * sessions / clusters tabs without the URL needing to carry chatboxId.
 */
export function useChatboxByHostId({
  isAuthenticated,
  hostId,
}: {
  isAuthenticated: boolean;
  hostId: string | null;
}) {
  const chatbox = useQuery(
    "chatboxes:getChatboxByHostId" as any,
    isAuthenticated && hostId ? ({ hostId } as any) : "skip",
  ) as ChatboxSettings | null | undefined;

  return {
    chatbox,
    isLoading: isAuthenticated && !!hostId && chatbox === undefined,
  };
}

export function useChatboxMutations() {
  // `createChatbox` / `duplicateChatbox` were removed with the 1:1
  // host↔chatbox refactor. To create, call `hosts.createHost` (which
  // auto-creates the publish-surface chatbox); to duplicate, call
  // `hosts.duplicateHost`. The remaining mutations are the publish-side
  // editors (mode, link rotation, members, name/description/chatUi).
  const updateChatbox = useMutation("chatboxes:updateChatbox" as any);
  const deleteChatbox = useMutation("chatboxes:deleteChatbox" as any);
  const setChatboxMode = useMutation("chatboxes:setChatboxMode" as any);
  const rotateChatboxLink = useMutation("chatboxes:rotateChatboxLink" as any);
  const upsertChatboxMember = useMutation(
    "chatboxes:upsertChatboxMember" as any,
  );
  const removeChatboxMember = useMutation(
    "chatboxes:removeChatboxMember" as any,
  );
  // Secure Guest Harness Enablement — project-admin gated per-swarm guest
  // execution + harness opt-in/caps editor.
  const setChatboxGuestExecution = useMutation(
    "chatboxes:setChatboxGuestExecution" as any,
  );

  return {
    updateChatbox,
    deleteChatbox,
    setChatboxMode,
    rotateChatboxLink,
    upsertChatboxMember,
    removeChatboxMember,
    setChatboxGuestExecution,
  };
}
